import { baseApi } from './baseApi';
import type { PaginationMeta } from '../../types';

import type { CurrencyCode } from '../../utils/moneda';
// ─── Types ────────────────────────────────────────────────────────────────────

export type CaptacionStatus =
  | 'llamando'
  | 'pre_captacion'
  | 'tasando'
  | 'listo'
  | 'descartado'
  | 'convertida';

export type CaptacionOperation = 'sale' | 'rent' | 'temporal_rent';

export interface CaptacionCoordinates { lat: number; lng: number }

export interface Captacion {
  _id: string;
  sourceUrl?: string;
  /** Aviso de la bolsa externa del que se captó (si aplica) → link de vuelta a la bolsa. */
  externalListingId?: string;
  /** La trajo "Gustavo capta por vos" (el agente la confirmó) → lleva el sello de Gustavo. */
  autoCaptured?: boolean;
  /** Quién la cargó: la pantalla o Gustavo desde el chat. El sello sale de acá también. */
  createdVia?: 'gustavo' | 'ui';
  /** ID de MercadoLibre extraído de la URL cuando sourcePortal='mercadolibre'. */
  meliId?: string;
  sourcePortal: string;
  operation: CaptacionOperation;
  propertyType: string;
  address: string;
  neighborhood?: string;
  city?: string;
  /** País, por NOMBRE. Vacío = Uruguay. Decide dónde se geocodifica. (05/09/2026) */
  country?: string;
  state?: string;
  padron?: string;
  coordinates?: CaptacionCoordinates;
  ownerCustomerId?: string;
  ownerName?: string;
  ownerPhone: string;
  estimatedPrice?: number;
  currency: CurrencyCode;
  bedrooms?: number;
  bathrooms?: number;
  totalArea?: number;
  /** Foto de portada (ej. traída del aviso al captar desde un portal). */
  imageUrl?: string;
  /** Galería completa del aviso original — siembra las fotos al convertir en propiedad. */
  images?: string[];
  /** Título del aviso original (solo referencia). */
  title?: string;
  /** Descripción del aviso original (solo referencia). */
  description?: string;
  notes?: string;
  /**
   * Lado de la operación:
   *   'captacion' → el cliente es el PROPIETARIO. Es lo que vive en el tablero.
   *   'oferta'    → el cliente es el COMPRADOR/INQUILINO y esto es su interés sobre una
   *                 propiedad de la cartera (registrar oferta, visita, reserva). Solo se
   *                 ve en la ficha del cliente; el backend lo excluye del tablero y de
   *                 los indicadores de captación.
   * Ausente en registros viejos → tratar como 'captacion'.
   */
  kind?: 'captacion' | 'oferta';
  status: CaptacionStatus;
  assignedTo: string;
  assignedToSnapshot: { name: string; lastName: string };
  appraisalValue?: number;
  appraisalNotes?: string;
  discardReason?: string;
  discardNote?: string;
  propertyId?: string;
  statusHistory: Array<{
    status: string;
    changedAt: string;
    changedBySnapshot: { name: string; lastName: string };
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaptacionBody {
  sourceUrl?: string;
  meliId?: string;
  sourcePortal: string;
  operation: CaptacionOperation;
  propertyType: string;
  address: string;
  neighborhood?: string;
  city?: string;
  /** País, por NOMBRE. Vacío = Uruguay. Decide dónde se geocodifica. (05/09/2026) */
  country?: string;
  state?: string;
  padron?: string;
  coordinates?: CaptacionCoordinates;
  ownerCustomerId?: string;
  ownerName?: string;
  ownerPhone: string;
  estimatedPrice?: number;
  currency?: CurrencyCode;
  bedrooms?: number;
  bathrooms?: number;
  totalArea?: number;
  notes?: string;
  /**
   * "Sí, creala igual aunque ese dueño ya se esté trabajando".
   *
   * El backend frena con 409 cuando hay captaciones vivas con el mismo teléfono y
   * devuelve cuáles son. No es un error: el mismo propietario puede tener otra propiedad
   * para captar, así que se le muestran al agente y decide. (06/08/2026)
   */
  confirmarDuplicado?: boolean;
}

export interface ListCaptacionesParams {
  status?: CaptacionStatus;
  operation?: CaptacionOperation;
  search?: string;
  page?: number;
  limit?: number;
  ownerCustomerId?: string;
  assignedTo?: string;
}

// ─── API slice ────────────────────────────────────────────────────────────────

/** `null` = ese link no lo tiene nadie. */
export type LinkCaptado = {
  captadaPor: string;
  oficina?:   string;
  estado:     string;
  fecha?:     string;
} | null;


/** Lo que hay alrededor de un punto. Ver `buscarAlrededor` en el backend. */
export type PropiedadCerca = {
  id: string; codigo?: string; titulo?: string; direccion?: string; barrio?: string;
  estado?: string; operacion?: string; precio?: number; moneda?: string; asesor?: string;
  distanciaM: number; ubicacionExacta: boolean;
};

export type CaptacionCerca = {
  id: string; direccion?: string; barrio?: string; estado: string;
  captadaPor: string; oficina?: string; operacion?: string; tipo?: string;
  desde?: string; distanciaM: number; esTuya: boolean;
};

export type PuntoResuelto = {
  lat: number; lng: number;
  precision: 'exacta' | 'calle' | 'barrio';
  fuente: 'maps' | 'padron' | 'direccion' | 'coordenadas';
  direccion?: string; padron?: string; departamento?: string;
};

export type Alrededor =
  | { ubicado: false; motivo: string }
  | {
      ubicado: true;
      punto: PuntoResuelto;
      radioM: number;
      propiedades: PropiedadCerca[];
      propiedadesAproximadas: PropiedadCerca[];
      captaciones: CaptacionCerca[];
      truncado: boolean;
    };

export type CercaParams = {
  mapsUrl?: string; address?: string; padron?: string; departamento?: string;
  lat?: number; lng?: number; metros?: number;
};

export const captacionesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({

    /**
     * "¿Este link ya lo está captando otro agente?" — lo dispara el botón Validar del
     * formulario de nueva captación.
     *
     * Consulta aparte y no un filtro del listado: un AGENTE solo ve SUS captaciones y la
     * pregunta es sobre las de los demás.
     */
    validarLinkCaptado: build.query<{ captada: LinkCaptado }, string>({
      query: (link) => ({ url: '/captaciones/validar-link', params: { link } }),
      transformResponse: (res: { data: { captada: LinkCaptado } }) => res.data,
    }),


    /**
     * "¿Hay algo captado en esta cuadra?" — el aviso del formulario mientras se carga la
     * dirección o el padrón.
     *
     * POST y no GET: acepta un link de Google Maps entero, y esos llevan `!`, `@` y comas
     * que en una query string se rompen.
     */
    buscarCerca: build.query<Alrededor, CercaParams>({
      query: (body) => ({ url: '/captaciones/cerca', method: 'POST', body }),
      transformResponse: (res: { data: Alrededor }) => res.data,
    }),

    listCaptaciones: build.query<{ data: Captacion[]; pagination: PaginationMeta }, ListCaptacionesParams>({
      query: (params) => ({ url: '/captaciones', params }),
      transformResponse: (res: { data: Captacion[]; pagination: PaginationMeta }) => res,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Captacion' as const, id: _id })),
              { type: 'Captacion' as const, id: 'LIST' },
            ]
          : [{ type: 'Captacion' as const, id: 'LIST' }],
    }),

    getCaptacion: build.query<Captacion, string>({
      query: (id) => `/captaciones/${id}`,
      transformResponse: (res: { data: Captacion }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Captacion' as const, id }],
    }),

    createCaptacion: build.mutation<Captacion, CreateCaptacionBody>({
      query: (body) => ({ url: '/captaciones', method: 'POST', body }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: [{ type: 'Captacion' as const, id: 'LIST' }],
    }),

    updateCaptacion: build.mutation<Captacion, { id: string; body: Partial<CreateCaptacionBody> }>({
      query: ({ id, body }) => ({ url: `/captaciones/${id}`, method: 'PUT', body }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Captacion' as const, id },
        { type: 'Captacion' as const, id: 'LIST' },
      ],
    }),

    updateCaptacionStatus: build.mutation<Captacion, { id: string; status: CaptacionStatus }>({
      query: ({ id, status }) => ({ url: `/captaciones/${id}/status`, method: 'PUT', body: { status } }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Captacion' as const, id },
        { type: 'Captacion' as const, id: 'LIST' },
      ],
    }),

    updateAppraisal: build.mutation<Captacion, { id: string; appraisalValue: number; appraisalNotes?: string }>({
      query: ({ id, appraisalValue, appraisalNotes }) => ({
        url: `/captaciones/${id}/appraisal`,
        method: 'PUT',
        body: { appraisalValue, appraisalNotes },
      }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Captacion' as const, id },
        { type: 'Captacion' as const, id: 'LIST' },
      ],
    }),

    // `convertCaptacion` quitado: la conversión ahora se dispara automáticamente
    // al guardar la propiedad con `captacionId` en el body.

    discardCaptacion: build.mutation<Captacion, { id: string; reason?: string; note?: string }>({
      query: ({ id, reason, note }) => ({
        url: `/captaciones/${id}/discard`, method: 'PUT', body: { reason, note },
      }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Captacion' as const, id },
        { type: 'Captacion' as const, id: 'LIST' },
      ],
    }),

    /** Borra descartadas: `ids` para una o varias, `todas: true` para todo el scope. */
    eliminarDescartadas: build.mutation<{ deleted: number; avisosLiberados: number }, { ids?: string[]; todas?: boolean }>({
      query: (body) => ({ url: '/captaciones/descartadas/eliminar', method: 'POST', body }),
      transformResponse: (res: { data: { deleted: number; avisosLiberados: number } }) => res.data,
      invalidatesTags: [{ type: 'Captacion' as const, id: 'LIST' }],
    }),

    reactivateCaptacion: build.mutation<Captacion, string>({
      query: (id) => ({ url: `/captaciones/${id}/reactivate`, method: 'PUT' }),
      transformResponse: (res: { data: Captacion }) => res.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'Captacion' as const, id },
        { type: 'Captacion' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useLazyValidarLinkCaptadoQuery,
  useLazyBuscarCercaQuery,
  useListCaptacionesQuery,
  useGetCaptacionQuery,
  useCreateCaptacionMutation,
  useUpdateCaptacionMutation,
  useUpdateCaptacionStatusMutation,
  useUpdateAppraisalMutation,
  useDiscardCaptacionMutation,
  useReactivateCaptacionMutation,
  useEliminarDescartadasMutation,
} = captacionesApi;
